import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Banknote, Coins, Download, Filter, Link2, Paperclip, Pencil, Plus, Search, Trash2, Unlink, X, Eye } from "lucide-react";
import AppShell from "../components/AppShell";
import DatePickerField from "../components/DatePickerField";
import ExportMenu from "../components/ExportMenu";
import { api, API_URL } from "../lib/api";

const PERIODS = [
  { id: "today", label: "Hoy" },
  { id: "week", label: "Semana" },
  { id: "month", label: "Mes" },
  { id: "year", label: "Año" },
  { id: "all", label: "Todo" },
];
const FISCAL_STATUS = [
  { id: "fiscal", label: "Fiscal", hint: "Cuenta para impuestos y reportes fiscales." },
  { id: "control_interno", label: "Control interno", hint: "Solo para tu caja real, no cuenta en impuestos." },
];

const money = (value) => `$${Number(value || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function today() {
  return new Date().toISOString().slice(0, 10);
}

function emptyForm() {
  return {
    movement_type: "ingreso",
    amount: "",
    date: today(),
    category: "",
    description: "",
    notes: "",
    counterparty: "",
    fiscal_status: "control_interno",
    payment_reference: "",
  };
}

export default function CashPage() {
  const [movements, setMovements] = useState([]);
  const [totals, setTotals] = useState({});
  const [categoryOptions, setCategoryOptions] = useState({ ingreso: [], egreso: [] });
  const [period, setPeriod] = useState("month");
  const [movementFilter, setMovementFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [reconOpen, setReconOpen] = useState(false);
  const [reconTarget, setReconTarget] = useState(null); // { movement, matches: [] }

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (period && period !== "all") params.set("period", period);
    if (categoryFilter) params.set("category", categoryFilter);
    if (statusFilter) params.set("fiscal_status", statusFilter);
    if (movementFilter) params.set("movement_type", movementFilter);
    if (search) params.set("search", search);
    try {
      const response = await api.get(`/cash/movements?${params.toString()}`, { withCredentials: true });
      setMovements(response.data.movements || []);
      setTotals(response.data.totals || {});
      setCategoryOptions(response.data.categories || { ingreso: [], egreso: [] });
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos cargar los movimientos.");
    }
  }, [period, categoryFilter, statusFilter, movementFilter, search]);

  useEffect(() => { load(); }, [load]);

  const openCreate = (type = "ingreso") => {
    setEditing(null);
    setForm({ ...emptyForm(), movement_type: type });
    setShowForm(true);
    setError("");
  };

  const openEdit = (movement) => {
    setEditing(movement);
    setForm({
      movement_type: movement.movement_type,
      amount: String(movement.amount || ""),
      date: movement.date,
      category: movement.category || "",
      description: movement.description || "",
      notes: movement.notes || "",
      counterparty: movement.counterparty || "",
      fiscal_status: movement.fiscal_status,
      payment_reference: movement.payment_reference || "",
    });
    setShowForm(true);
    setError("");
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editing) {
        const src = editing.source || "cash";
        await api.put(`/cash/movements/${editing.id}?source=${src}`, payload, { withCredentials: true });
      } else {
        await api.post("/cash/movements", payload, { withCredentials: true });
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyForm());
      load();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos guardar el movimiento.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (movement) => {
    if (!window.confirm("¿Eliminar este movimiento?")) return;
    const src = movement.source || "cash";
    await api.delete(`/cash/movements/${movement.id}?source=${src}`, { withCredentials: true });
    load();
  };

  const toggleFiscalStatus = async (movement) => {
    // For income/expense sources this flips payment_method away from Efectivo (leaves Caja).
    if (movement.source !== "cash") {
      if (!window.confirm("Este movimiento vive en Ingresos/Egresos. Si lo cambias a Control interno saldrá de Caja y su método de pago pasará a Transferencia. ¿Continuar?")) return;
    }
    const next = movement.fiscal_status === "fiscal" ? "control_interno" : "fiscal";
    const src = movement.source || "cash";
    await api.patch(`/cash/movements/${movement.id}?source=${src}`, { fiscal_status: next }, { withCredentials: true });
    load();
  };

  const uploadReceipt = async (id, file) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    try {
      await api.post(`/cash/movements/${id}/receipt`, body, { withCredentials: true });
      load();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos subir el comprobante.");
    }
  };

  const showReceipt = async (id) => {
    const token = window.localStorage.getItem("session_token");
    try {
      const resp = await fetch(`${API_URL}/cash/movements/${id}/receipt`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      setPreview({ url: URL.createObjectURL(blob), type: blob.type });
    } catch (_) {
      setError("No pudimos abrir el comprobante.");
    }
  };

  const openReconciliation = async (movementId) => {
    try {
      const response = await api.get(`/cash/movements/${movementId}/matches`, { withCredentials: true });
      setReconTarget(response.data);
      setReconOpen(true);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos buscar duplicados.");
    }
  };

  const linkMovement = async (movementId, type, id) => {
    try {
      await api.post(`/cash/movements/${movementId}/link`, { type, id }, { withCredentials: true });
      setReconOpen(false);
      setReconTarget(null);
      load();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos vincular.");
    }
  };

  const unlinkMovement = async (movementId) => {
    if (!window.confirm("¿Desvincular este movimiento del registro fiscal?")) return;
    try {
      await api.post(`/cash/movements/${movementId}/unlink`, {}, { withCredentials: true });
      load();
    } catch (_) { /* noop */ }
  };

  const currentCategories = form.movement_type === "ingreso" ? categoryOptions.ingreso : categoryOptions.egreso;

  const kpis = useMemo(() => ([
    { key: "balance", label: "Saldo en caja", value: money(totals.balance || 0), tone: "primary" },
    { key: "in", label: "Ingresos en efectivo", value: money(totals.total_income || 0), tone: "success" },
    { key: "out", label: "Egresos en efectivo", value: money(totals.total_expense || 0), tone: "danger" },
    { key: "fiscal", label: "Fiscal (cuenta impuestos)", value: `${money(totals.fiscal_income || 0)} · ${money(totals.fiscal_expense || 0)}`, tone: "muted" },
    { key: "internal", label: "Control interno", value: `${money(totals.internal_income || 0)} · ${money(totals.internal_expense || 0)}`, tone: "muted" },
  ]), [totals]);

  return (
    <AppShell>
      <div className="page cash-page">
        <section className="cash-hero">
          <div>
            <small className="eyebrow">CAJA · EFECTIVO REAL</small>
            <h1>Tu dinero <em>en la mano.</em></h1>
            <p>Registra el efectivo que entra y sale. Clasifica cada movimiento según corresponda para llevar un mejor control financiero y fiscal.</p>
          </div>
          <div className="cash-hero-actions">
            <ExportMenu resource="cash" testid="cash-export" label="Exportar mes" />
            <button type="button" className="cash-btn success" onClick={() => openCreate("ingreso")} data-testid="cash-add-income-button">
              <Plus size={16} /> Ingreso en efectivo
            </button>
            <button type="button" className="cash-btn danger" onClick={() => openCreate("egreso")} data-testid="cash-add-expense-button">
              <Plus size={16} /> Egreso en efectivo
            </button>
          </div>
        </section>

        <section className="cash-kpis" data-testid="cash-kpis">
          {kpis.map((kpi) => (
            <article key={kpi.key} className={`cash-kpi cash-kpi-${kpi.tone}`}>
              <small>{kpi.label}</small>
              <b>{kpi.value}</b>
            </article>
          ))}
        </section>

        <section className="cash-filters" data-testid="cash-filters">
          <div className="cash-filters-group">
            <Filter size={14} />
            {PERIODS.map((p) => (
              <button key={p.id} type="button" className={period === p.id ? "active" : ""} onClick={() => setPeriod(p.id)} data-testid={`cash-period-${p.id}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="cash-filters-group">
            <select value={movementFilter} onChange={(e) => setMovementFilter(e.target.value)} data-testid="cash-filter-type">
              <option value="">Todos los tipos</option>
              <option value="ingreso">Solo ingresos</option>
              <option value="egreso">Solo egresos</option>
            </select>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="cash-filter-category">
              <option value="">Todas las categorías</option>
              {[...categoryOptions.ingreso, ...categoryOptions.egreso].map((c) => <option key={c}>{c}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="cash-filter-status">
              <option value="">Fiscal + Control</option>
              {FISCAL_STATUS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <label className="cash-search">
              <Search size={14} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar concepto, persona, nota..." data-testid="cash-search" />
            </label>
          </div>
        </section>

        {error && <p className="profile-error" data-testid="cash-error">{error}</p>}

        {(totals.potential_duplicates || 0) > 0 && (
          <div className="cash-recon-banner" data-testid="cash-recon-banner">
            <AlertTriangle size={20} />
            <div>
              <b>Posibles duplicados detectados</b>
              <small>
                Tenemos {totals.potential_duplicates} movimiento{totals.potential_duplicates === 1 ? "" : "s"} fiscal{totals.potential_duplicates === 1 ? "" : "es"} que
                {totals.potential_duplicates === 1 ? " parece" : " parecen"} coincidir con registros de Ingresos o Egresos.
                Vincúlalos para evitar contarlos dos veces en impuestos.
              </small>
            </div>
          </div>
        )}

        <section className="cash-table-wrap">
          <table className="cash-table" data-testid="cash-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Concepto</th>
                <th>Categoría</th>
                <th>Persona</th>
                <th>Estatus</th>
                <th className="right">Monto</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {movements.length === 0 && (
                <tr><td colSpan={7} className="cash-empty">Aún no hay movimientos en el período seleccionado.</td></tr>
              )}
              {movements.map((m) => (
                <tr key={`${m.source || "cash"}-${m.id}`} data-testid={`cash-row-${m.id}`} className={m.movement_type === "ingreso" ? "row-in" : "row-out"}>
                  <td>{m.date}</td>
                  <td>
                    <b>{m.description || "Sin concepto"}</b>
                    {m.notes && <small>{m.notes}</small>}
                    {m.source === "income" && (
                      <span className="cash-source-badge income" data-testid={`cash-source-${m.id}`} title="Este ingreso está en Ingresos con método Efectivo">
                        <Link2 size={11} /> desde Ingresos
                      </span>
                    )}
                    {m.source === "expense" && (
                      <span className="cash-source-badge expense" data-testid={`cash-source-${m.id}`} title="Este egreso está en Egresos con método Efectivo">
                        <Link2 size={11} /> desde Egresos
                      </span>
                    )}
                  </td>
                  <td>{m.category}</td>
                  <td>{m.counterparty || "—"}</td>
                  <td>
                    <button
                      type="button"
                      className={`cash-status-badge ${m.fiscal_status}`}
                      onClick={() => toggleFiscalStatus(m)}
                      title={FISCAL_STATUS.find((s) => s.id === m.fiscal_status)?.hint}
                      data-testid={`cash-status-toggle-${m.id}`}
                    >
                      {m.fiscal_status === "fiscal" ? "Fiscal" : "Interno"}
                    </button>
                    {m.is_linked && (
                      <button
                        type="button"
                        className="cash-link-badge linked"
                        onClick={() => unlinkMovement(m.id)}
                        title="Vinculado a un registro fiscal · click para desvincular"
                        data-testid={`cash-linked-badge-${m.id}`}
                      >
                        <Link2 size={12} /> Vinculado
                      </button>
                    )}
                    {m.has_potential_duplicate && !m.is_linked && (
                      <button
                        type="button"
                        className="cash-link-badge warn"
                        onClick={() => openReconciliation(m.id)}
                        title="Posible duplicado · revisa y vincula"
                        data-testid={`cash-duplicate-badge-${m.id}`}
                      >
                        <AlertTriangle size={12} /> Duplicado
                      </button>
                    )}
                  </td>
                  <td className={`right ${m.movement_type === "ingreso" ? "amount-in" : "amount-out"}`}>
                    {m.movement_type === "ingreso" ? "+" : "−"} {money(m.amount)}
                  </td>
                  <td className="cash-actions">
                    {m.source === "cash" && m.has_receipt && (
                      <button type="button" onClick={() => showReceipt(m.id)} title="Ver comprobante" data-testid={`cash-view-receipt-${m.id}`}><Eye size={14} /></button>
                    )}
                    {m.source === "cash" && (
                      <label title="Adjuntar comprobante" data-testid={`cash-attach-label-${m.id}`}>
                        <Paperclip size={14} />
                        <input type="file" accept="application/pdf,image/*" style={{ display: "none" }} onChange={(e) => uploadReceipt(m.id, e.target.files?.[0])} data-testid={`cash-attach-${m.id}`} />
                      </label>
                    )}
                    <button type="button" onClick={() => openEdit(m)} title="Editar" data-testid={`cash-edit-${m.id}`}><Pencil size={14} /></button>
                    <button type="button" onClick={() => remove(m)} title="Eliminar" data-testid={`cash-delete-${m.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {showForm && (
          <div className="cash-modal-backdrop" onClick={() => setShowForm(false)}>
            <div className="cash-modal" onClick={(e) => e.stopPropagation()} data-testid="cash-modal">
              <header>
                <div>
                  <small>{editing ? "Editar movimiento" : "Nuevo movimiento en efectivo"}</small>
                  <b>{form.movement_type === "ingreso" ? "Ingreso" : "Egreso"} · {form.fiscal_status === "fiscal" ? "Fiscal" : "Control interno"}</b>
                </div>
                <button type="button" onClick={() => setShowForm(false)} data-testid="cash-modal-close"><X size={18} /></button>
              </header>
              <form onSubmit={save}>
                <div className="cash-modal-grid">
                  <label>Tipo
                    <select value={form.movement_type} onChange={(e) => setForm({ ...form, movement_type: e.target.value, category: "" })} data-testid="cash-form-type">
                      <option value="ingreso">Ingreso</option>
                      <option value="egreso">Egreso</option>
                    </select>
                  </label>
                  <label>Monto
                    <input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="cash-form-amount" />
                  </label>
                  <label>Fecha
                    <DatePickerField value={form.date} onChange={(value) => setForm({ ...form, date: value })} testid="cash-form-date" />
                  </label>
                  <label>Categoría
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required data-testid="cash-form-category">
                      <option value="">Selecciona</option>
                      {currentCategories.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </label>
                  <label className="full">Concepto
                    <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Descripción corta" data-testid="cash-form-description" />
                  </label>
                  <label>Cliente / Proveedor
                    <input value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} placeholder="Opcional" data-testid="cash-form-counterparty" />
                  </label>
                  <label>Referencia
                    <input value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} placeholder="Ticket #, folio..." data-testid="cash-form-reference" />
                  </label>
                  <label className="full">Notas
                    <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Detalles adicionales" data-testid="cash-form-notes" />
                  </label>
                </div>

                <fieldset className="cash-status-picker" data-testid="cash-form-status-picker">
                  <legend>Estatus fiscal</legend>
                  {FISCAL_STATUS.map((s) => (
                    <label key={s.id} className={form.fiscal_status === s.id ? "active" : ""}>
                      <input type="radio" name="fiscal_status" value={s.id} checked={form.fiscal_status === s.id} onChange={() => setForm({ ...form, fiscal_status: s.id })} data-testid={`cash-form-fiscal-${s.id}`} />
                      <div>
                        <b>{s.label}</b>
                        <small>{s.hint}</small>
                      </div>
                    </label>
                  ))}
                </fieldset>

                {error && <p className="profile-error" data-testid="cash-form-error">{error}</p>}
                <div className="cash-modal-actions">
                  <button type="button" onClick={() => setShowForm(false)} className="cash-btn ghost" data-testid="cash-form-cancel">Cancelar</button>
                  <button type="submit" className="cash-btn primary" disabled={saving} data-testid="cash-form-save">
                    {saving ? "Guardando..." : editing ? "Guardar cambios" : "Registrar movimiento"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {preview && (
          <div className="business-doc-preview" onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }} data-testid="cash-preview-modal">
            <div className="business-doc-preview-inner" onClick={(e) => e.stopPropagation()}>
              <header>
                <b>Comprobante</b>
                <button type="button" onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }}><X size={18} /></button>
              </header>
              {preview.type?.startsWith("image/") ? <img src={preview.url} alt="Comprobante" /> : <iframe title="Comprobante" src={preview.url} />}
            </div>
          </div>
        )}

        {reconOpen && reconTarget && (
          <div className="cash-modal-backdrop" onClick={() => setReconOpen(false)}>
            <div className="cash-modal cash-recon-modal" onClick={(e) => e.stopPropagation()} data-testid="cash-recon-modal">
              <header>
                <div>
                  <small>RECONCILIACIÓN</small>
                  <b>Posibles registros duplicados</b>
                </div>
                <button type="button" onClick={() => setReconOpen(false)} data-testid="cash-recon-close"><X size={18} /></button>
              </header>
              <div className="cash-recon-body">
                <article className="cash-recon-source" data-testid="cash-recon-source">
                  <small>Movimiento en efectivo</small>
                  <b>{reconTarget.movement.description || "Sin concepto"}</b>
                  <span>{reconTarget.movement.date} · {money(reconTarget.movement.amount)} · {reconTarget.movement.counterparty || "—"}</span>
                </article>
                <p className="cash-recon-help">
                  Vincula este movimiento a un registro fiscal existente para que no cuente dos veces en tus impuestos.
                </p>
                {reconTarget.matches.length === 0 ? (
                  <p className="cash-recon-empty" data-testid="cash-recon-empty">
                    No encontramos coincidencias. Puedes cerrar este diálogo o cambiarlo a "Control interno" si no debe contar para impuestos.
                  </p>
                ) : (
                  <ul className="cash-recon-list">
                    {reconTarget.matches.map((match) => (
                      <li key={`${match.type}-${match.id}`} data-testid={`cash-recon-match-${match.id}`}>
                        <div>
                          <b>{match.concept || "Sin concepto"}</b>
                          <small>{match.date} · {money(match.amount)} · {match.party || "—"} · {match.category || "Sin categoría"}</small>
                        </div>
                        <button
                          type="button"
                          className="cash-btn primary"
                          onClick={() => linkMovement(reconTarget.movement.id, match.type, match.id)}
                          data-testid={`cash-recon-link-${match.id}`}
                        >
                          <Link2 size={14} /> Vincular
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
